import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';

function FAD() {
  const [pressure, setPressure] = useState('');
  const [temperature, setTemperature] = useState('');
  const [humidity, setHumidity] = useState('');
  const [flowRate, setFlowRate] = useState('');

  const { result, error } = useMemo(() => {
    const p2 = parseNumber(pressure);
    const tC = parseNumber(temperature);
    const rh = parseNumber(humidity);
    const q = parseNumber(flowRate);

    if (p2 == null || tC == null || rh == null || q == null) {
      return { result: null, error: '' };
    }

    if (p2 <= 0) {
      return { result: null, error: 'Pressure must be greater than zero' };
    }
    if (rh < 0 || rh > 100) {
      return { result: null, error: 'Humidity must be between 0 and 100' };
    }

    const p1 = 1.01325;
    const t1 = 293.15;
    const t2 = tC + 273.15;
    const fad = q * (p1 / p2) * (t2 / t1) * (1 - rh / 100);
    return { result: fad, error: '' };
  }, [pressure, temperature, humidity, flowRate]);

  return (
    <ToolLayout title="Free Air Delivery">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Estimate FAD from a measured compressed-air flow, pressure, temperature, and humidity.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <NumberField label="Compressed air pressure (bar)" value={pressure} onChange={setPressure} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Temperature (°C)" value={temperature} onChange={setTemperature} min={-273.15} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Relative humidity (%)" value={humidity} onChange={setHumidity} min={0} max={100} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Measured flow (L/min)" value={flowRate} onChange={setFlowRate} min={0} />
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result != null && (
            <ResultPanel
              items={[
                { label: 'Free air delivery', value: `${formatNumber(result, 2)} L/min`, fullWidth: true },
                { label: 'FAD (m³/min)', value: formatNumber(result / 1000, 3) },
                { label: 'FAD (CFM)', value: formatNumber(result * 0.0353147, 2) },
              ]}
            />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        FAD = Q × (P1 / P2) × (T2 / T1) × (1 − RH / 100), with P1 = 1.01325 bar and T1 = 20 °C. This is a simplified correction; ISO 1217 tests use a fuller humidity and inlet-condition method.
      </Typography>
    </ToolLayout>
  );
}

export default FAD;
